/**
 * Der Verlauf eines Messkonzepts — die Zeile „Verlauf" aus
 * `ui_kits/dashboard/MesskonzeptScreen.jsx`.
 *
 * **Der Entwurf führt ein Änderungsprotokoll; der Server führt Fassungen.** Ein
 * Protokoll gibt es nicht, und eines hier zu erfinden hieße, Sätze über
 * Änderungen zu schreiben, die niemand festgehalten hat. Was es gibt, ist jede
 * Fassung mit ihrer Gültigkeit, ihrer Vorlage, ihrer Zuordnung und ihrem Layout
 * — und damit lässt sich der Satz **ablesen**: Eine Fassung unterscheidet sich
 * von der, die sie abgelöst hat, in genau dem, was jemand geändert hat.
 *
 * **Verglichen wird mit der Vorgängerin nach Nummer, sortiert wird nach
 * Zeitpunkt.** Eine Korrektur trägt die höhere Nummer und den früheren
 * Zeitpunkt; nach Nummer sortiert stünde sie als „zuletzt" oben, obwohl sie ab
 * einem Tag gilt, der vor der vorigen Änderung liegt. Verglichen werden muss sie
 * trotzdem mit der Fassung, die sie ersetzt hat — sonst stünde da, was sich
 * gegenüber einer ganz anderen geändert hätte.
 *
 * Reine Rechnung ohne React und ohne Übersetzung; die Wörter kommen herein.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { isIntakeInput } from "./concept-editor.js";

type Concept = ApiComponents["schemas"]["MeasurementConcept"];

export interface ConceptChange {
  readonly id: string;
  readonly version: number;
  /** Ab wann die Fassung gilt. */
  readonly at: string;
  readonly text: string;
  /** Die Fassung gilt ab einem Zeitpunkt vor dem ihrer Vorgängerin — eine Korrektur. */
  readonly retroactive: boolean;
  /** Eine spätere Korrektur hat sie ganz ersetzt; gerechnet wird mit ihr nicht mehr. */
  readonly replaced: boolean;
}

export interface ConceptHistoryWords {
  /** Der Name einer Vorlage — `null`, wenn es keine ist (freier Graph). */
  readonly templateLabel: (key: Concept["templateKey"]) => string | null;
  /** Der Name des Steckplatzes, zu dem ein Eingang gehört — `null`, wenn unbekannt. */
  readonly slotLabelOfInput: (inputKey: string) => string | null;
  readonly distributionLabel: (distribution: NonNullable<Concept["layout"]>["distribution"]) => string;
}

type Source = { readonly meteringPointId: string | null; readonly role: string | null };

function sourcesOf(concept: Concept): ReadonlyMap<string, Source> {
  return new Map(
    concept.nodes
      .filter((node) => node.type === "meter_register")
      .map((node) => [node.key, { meteringPointId: node.meteringPointId, role: node.registerRole }]),
  );
}

function changedInputs(before: Concept, after: Concept): readonly string[] {
  const a = sourcesOf(before);
  const b = sourcesOf(after);
  const keys = new Set([...a.keys(), ...b.keys()]);
  return [...keys].filter((key) => {
    const x = a.get(key);
    const y = b.get(key);
    return (
      (x?.meteringPointId ?? null) !== (y?.meteringPointId ?? null) ||
      (x?.role ?? null) !== (y?.role ?? null)
    );
  });
}

function describe(concept: Concept, before: Concept | undefined, words: ConceptHistoryWords): string {
  const label = words.templateLabel(concept.templateKey) ?? concept.name;
  if (before === undefined) return `Messkonzept „${label}" eingerichtet`;
  if (before.templateKey !== concept.templateKey) return `Messkonzept geändert auf „${label}"`;

  const parts: string[] = [];
  // Die Aufnahme steht unten in eigenen Worten (#43), nicht als „Zuordnung geändert".
  const inputs = changedInputs(before, concept).filter((key) => !isIntakeInput(key));
  if (inputs.length > 0) {
    const labels = [...new Set(inputs.map(words.slotLabelOfInput))];
    const named = labels.filter((entry): entry is string => entry !== null);
    parts.push(
      named.length === 0
        ? "Zuordnung geändert"
        : `Zuordnung geändert: ${named.join(", ")}${named.length < labels.length ? " und weitere" : ""}`,
    );
  }
  const layoutBefore = before.layout;
  const layoutAfter = concept.layout;
  if (layoutBefore !== null && layoutAfter !== null) {
    if (
      layoutBefore.generatorCount !== layoutAfter.generatorCount ||
      JSON.stringify(layoutBefore.consumers) !== JSON.stringify(layoutAfter.consumers)
    ) {
      parts.push("Verbraucher oder Erzeuger geändert");
    }
    /* Je Erzeugungszähler (#43): Welche Aufnahme dazukam oder wegfiel, steht im Graphen — auch
       wenn `generationIntake` („mindestens einer") gleich blieb. */
    const intakesBefore = intakeKeys(before);
    const intakesAfter = intakeKeys(concept);
    const gained = [...intakesAfter].filter((key) => !intakesBefore.has(key));
    const lost = [...intakesBefore].filter((key) => !intakesAfter.has(key));
    const nameOf = (key: string) => words.slotLabelOfInput(key) ?? "Erzeugungszähler";
    for (const key of gained) parts.push(`${nameOf(key)} zählt jetzt auch, was hineingeht`);
    for (const key of lost) parts.push(`${nameOf(key)} zählt nur noch, was herausgeht`);
    if (
      gained.length === 0 &&
      lost.length === 0 &&
      layoutBefore.generationIntake !== layoutAfter.generationIntake
    ) {
      parts.push(
        layoutAfter.generationIntake
          ? "Erzeugungszähler zählt jetzt auch, was hineingeht"
          : "Erzeugungszähler zählt nur noch, was herausgeht",
      );
    }
    if (layoutBefore.distribution !== layoutAfter.distribution) {
      parts.push(`Verteilung des PV-Stroms: ${words.distributionLabel(layoutAfter.distribution)}`);
    }
  }
  /* „Fassung erneuern" ändert nichts an der Zuordnung und ist trotzdem eine
     Fassung — sie rechnet nach der heutigen Vorlage oder nimmt eine Wohneinheit
     auf. Ein leerer Satz sähe aus wie ein Fehler in dieser Liste. */
  return parts.length === 0 ? "Rechnung aktualisiert, Zuordnung unverändert" : parts.join(" · ");
}

/** Die Eingänge der Aufnahme, die eine Fassung zählt. */
function intakeKeys(concept: Concept): ReadonlySet<string> {
  return new Set(
    concept.nodes
      .filter((node) => isIntakeInput(node.key) && node.meteringPointId !== null)
      .map((node) => node.key),
  );
}

/** Der Verlauf, jüngster Zeitpunkt zuerst. */
export function conceptHistory(
  concepts: readonly Concept[],
  words: ConceptHistoryWords,
): readonly ConceptChange[] {
  const byVersion = [...concepts].sort((a, b) => a.version - b.version);
  const changes = byVersion.map((concept, index): ConceptChange => {
    const before = index === 0 ? undefined : byVersion[index - 1];
    return {
      id: concept.id,
      version: concept.version,
      at: concept.validFrom,
      text: describe(concept, before, words),
      retroactive: before !== undefined && concept.validFrom < before.validFrom,
      replaced: concept.validTo !== null && concept.validTo <= concept.validFrom,
    };
  });
  return changes.sort((a, b) =>
    a.at < b.at ? 1 : a.at > b.at ? -1 : b.version - a.version,
  );
}

/**
 * Seit wann **dieses Konzept** gilt — nicht die jüngste Fassung. Ein
 * Zählerwechsel ist eine neue Fassung, aber kein neues Konzept; stünde sein
 * Zeitpunkt unter „Konzept gültig ab", sähe ein Tausch vom Dienstag aus wie ein
 * Konzeptwechsel vom Dienstag.
 *
 * Gezählt wird rückwärts über die Fassungen, solange sie dieselbe Vorlage
 * tragen und tatsächlich gegolten haben.
 */
export function conceptSince(concepts: readonly Concept[], active: Concept): string {
  const byVersion = [...concepts]
    .filter((concept) => concept.version <= active.version)
    .filter((concept) => concept === active || concept.validTo === null || concept.validTo > concept.validFrom)
    .sort((a, b) => b.version - a.version);
  let since = active.validFrom;
  for (const concept of byVersion) {
    if (concept.templateKey !== active.templateKey) break;
    if (concept.validFrom < since) since = concept.validFrom;
  }
  return since;
}

/**
 * Seit wann es an dieser Anlage **überhaupt** ein Messkonzept gibt — die
 * älteste Fassung, über alle Vorlagen hinweg. Das ist der Beginn für einen
 * **Konzeptwechsel** „seit Beginn": Wer die Vorlage von Anfang an falsch gewählt
 * hat, ersetzt damit alles. Für eine Änderung **innerhalb** eines Konzepts ist
 * es der falsche Beginn — der Server ersetzt jede Fassung ab `validFrom`, und
 * eine Korrektur in Vorlage B rechnete die Zeit von Vorlage A mit B neu. Dort
 * gilt `conceptSince` (HM-190).
 */
export function firstConceptStart(concepts: readonly Concept[]): string | null {
  return concepts.reduce<string | null>(
    (earliest, concept) => (earliest === null || concept.validFrom < earliest ? concept.validFrom : earliest),
    null,
  );
}

/**
 * Die jüngste Änderung, die tatsächlich gegolten hat — für „zuletzt ab". Der
 * Verlauf steht nach Zeitpunkt sortiert, und vorn kann eine Fassung stehen, die
 * eine spätere Korrektur ganz ersetzt hat (März, dann rückwirkend ab Februar):
 * Sie hat nie gerechnet, und „zuletzt ab März" wäre eine Änderung, die es in
 * der Rechnung nicht gibt.
 */
export function latestEffectiveChange(history: readonly ConceptChange[]): ConceptChange | undefined {
  return history.find((entry) => !entry.replaced);
}
